// client/src/utils/aiSummarySections.js
// AI 策略摘要（/strategy/preview 回傳的 summary）切段用的純函式。沒有 React、沒有副作用，可以直接用 node 測。
//
// ⚠️ 2026-09-29（A11）取代 StrategySummaryForm 的 extractStrategyLogic。舊寫法：
//     text.split(/策略邏輯[：:\s]+|Strategy Logic[：:\s]+|Logic[：:\s]+/i) 取最後一段
//   三個問題：
//   (1)「風控」排在策略邏輯前面 → 被切掉，使用者看不到自己的策略實際用了哪個停損；
//   (2) 簡中「策略逻辑」比對不到 → 簡中反而顯示全文（連表頭一起），三種語系行為不一致；
//   (3) `Logic[：:\s]+` 會比對到內文任何「logic＋空白」→ 英文摘要只剩最後半句；
//       繁中內文出現「策略邏輯＋空白」也會被截；「**策略邏輯**：」粗體比對不到 → 顯示全文。
//
// 摘要格式出自 server/AI_modules/mas_limit_{zh,cn,en}.txt（四個介面語系對應三種摘要：zh-TW→zh、zh-CN→cn、
//   en-US／en-GB→en）。三份規定的欄位與順序一樣：表頭 7 行，接著 進場條件、出場條件、風控、策略邏輯。
//
// 做法：逐行比對「行首」出現的已知標題，每一段到下一個「分段點」為止。只認行首 → 內文提到「策略邏輯」「logic」
//   「觸發風控:」都不會被誤切。
//   行首前綴：空白、Markdown 標題 #、引言 >、項目符號（- + • · ● ▪ – — ▶ ■ ★ 等符號）、emoji（🛡️ 📌 1️⃣）、
//     編號（1. 1) 1、 (1) （1） ① 一、 （一））、粗體 ** 或 __、括號 【 [。
//   標題後：可接括號說明（風控（停損）、Risk Control (Stop Loss)），再接全形「：」或半形「:」、破折號 — –、
//     「 - 」、閉括號 】，或標題獨佔一行（### 策略邏輯⏎…）。英文不分大小寫、Condition(s)／Control(s) 單複數都收。
//   一整段擠在同一行、用全形空白隔開（「商品：XAUUSD　進場條件：…　策略邏輯：…」）→ 先在全形空白處斷行再解析。
//
// 分段點規則（2026-09-29 審查 F1／A11-3 後改）：原則是「寧可多顯示、不可靜默丟內容」。
//   (a) 表頭 7 欄（商品…迄日）只在第一個內文段（進場／出場／風控／策略邏輯）出現前算分段點；之後出現的
//       「Capital: fully allocated…」「本金：…」是內文，併進目前這段。
//   (b) 已經有內容的標題再出現：只有「整段逐字重複」才當分段點（丟掉重複段 —— dev DB 存檔版把同一段重複三次）；
//       否則是內文（例如策略邏輯裡分點寫「- 進場條件：超賣區逢低布局」），併進目前這段。
//   (c) 策略邏輯開始之後，還沒有內容的標題只有「風控」或「策略邏輯」本身（前面是空的「策略邏輯：」）、而且跟
//       「策略邏輯」那行同一種格式（縮排、項目符號／編號種類、粗體）才算分段點
//       → 「風控」排在策略邏輯後面仍切得出來；邏輯裡用不同格式分點寫的「1. 風控：以 ATR 停損…」不會被誤當風控。
//       進場／出場／表頭在策略邏輯之後一律不開新段（已有內容的逐字重複仍依 (b) 丟掉）：畫面只顯示策略邏輯與風控，
//       切進 entry／exit 段的內容等於靜默消失。例：前面的出場標題寫成「出場規則：」「Exit Rules:」（比對不到）或漏寫，
//       邏輯裡同格式的「出場條件：RSI 回到 70 以上…」「- 進場條件：…」會把邏輯截斷、後文全丟（2026-09-29 複驗 V-A11-R1）。
//       代價：AI 若真的把出場條件排在策略邏輯之後，那段會併進邏輯一起顯示（多顯示，跟 main 舊寫法一樣）。
//   (d) 「停損：／止损：／Stop Loss:」是風控別名（AI 省略「風控：」直接拿 prompt 預設句開頭當欄位名）：
//       只有在風控還沒出現、策略邏輯也還沒開始時才算分段點，內容保留「停損：」字樣；
//       「風控：⏎停損：…」這種巢狀寫法，停損那行屬於風控段。
//
// 找不到行首的策略邏輯標題時（getSummaryDisplay）：退回舊寫法的精神 —— 找第一個「策略邏輯／策略逻辑／
//   Strategy Logic＋冒號或破折號」取之後的文字（拿掉會誤切內文的裸 `Logic` 與「只接空白」）；還是找不到才顯示全文。

const WS = "[ \\t\\u3000]";
const BOLD = "(?:\\*\\*|__)";

// 標題寫成 regex 片段（不是純文字），英文的空白允許多個。順序＝回傳物件的 key。
const SECTION_LABELS = [
  ["instrument", ["商品", "Instrument"]],
  ["timeframe", ["交易週期", "交易周期", "Timeframe"]],
  ["capital", ["本金", "Capital"]],
  ["lotSize", ["手數", "手数", "Lot[ \\t]+Size"]],
  ["orderType", ["下單類型", "下单类型", "Order[ \\t]+Type"]],
  ["startDate", ["起日", "Start[ \\t]+Date"]],
  ["endDate", ["迄日", "訖日", "End[ \\t]+Date"]],
  ["entry", ["進場條件", "进场条件", "Entry[ \\t]+Conditions?"]],
  ["exit", ["出場條件", "出场条件", "Exit[ \\t]+Conditions?"]],
  ["risk", ["風控", "风控", "風險控制", "风险控制", "風險管理", "风险管理", "Risk[ \\t]+Controls?", "Risk[ \\t]+Management"]],
  ["logic", ["策略邏輯", "策略逻辑", "Strategy[ \\t]+Logic", "Logic"]],
];
// 風控別名（規則 (d)），解析結果一樣放在 risk
const RISK_ALIAS_LABELS = ["停損", "停损", "止損", "止损", "Stop[ \\t-]?Loss"];

export const SECTION_KEYS = SECTION_LABELS.map(([key]) => key);
const HEADER_KEYS = new Set(SECTION_KEYS.slice(0, 7));
// 規則 (c)：策略邏輯開始之後，只有畫面會顯示的段（風控、策略邏輯）還能開新段；進場／出場／表頭不行
const SHOWN_AFTER_LOGIC = new Set(["risk", "logic"]);
const ALIAS_INDEX = SECTION_KEYS.length; // 捕捉群組裡別名排在最後

// 行首前綴的單一記號
const MARK =
  "(?:#{1,6}" +
  "|>" +
  "|\\d\\uFE0F?\\u20E3" + // 1️⃣
  "|\\d{1,2}[.)\\u3001\\uFF0E\\uFF09]" + // 1. 1) 1、 1． 1）
  "|[(\\uFF08]\\d{1,2}[)\\uFF09]" + // (1) （1）
  "|[一二三四五六七八九十]{1,3}[\\u3001.\\uFF0E]" + // 一、
  "|[(\\uFF08][一二三四五六七八九十]{1,3}[)\\uFF09]" + // （一）
  "|\\*(?=" + WS + ")" + // 「* 」項目符號（「**」是粗體，另外處理）
  "|[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]" + // emoji（UTF-16 代理對；不用 u 旗標，舊瀏覽器也能跑）
  "|[-+\\u00B7\\u2010-\\u2015\\u2022\\u2023\\u2043\\u2190-\\u2BFF\\u30FB\\uFF0D\\uFF5E]" + // – — • ▶ ■ ● ★ ✅ ① 等
  ")[\\uFE0F\\u200D]*";

// 群組 1＝縮排、2＝記號、3＝粗體／開括號；群組 4 起＝各標題
const LINE_PREFIX =
  "^(" + WS + "*)" +
  "((?:" + MARK + WS + "*){0,4})" +
  "(" + BOLD + "?" + WS + "*[\\u3010\\u3014\\[]?" + WS + "*" + BOLD + "?" + WS + "*)";

// 標題後的括號說明：風控（停損）、Risk Control (Stop Loss)
const PAREN = "(?:" + WS + "*[(\\uFF08][^()\\uFF08\\uFF09\\r\\n]{1,30}[)\\uFF09])?";

const DELIM =
  "(?:" +
  BOLD + "?" + WS + "*[\\u3011\\u3015\\]]" + WS + "*" + BOLD + "?" + WS + "*[\\uFF1A:]?" + // 【風控】 【風控】：
  "|" + WS + "*" + BOLD + "?" + WS + "*[\\uFF1A:]" + // ： :
  "|" + WS + "*" + BOLD + "?" + WS + "*[\\u2014\\u2013]" + // — –
  "|" + WS + "+" + BOLD + "?-(?=" + WS + ")" + // 「 - 」
  "|" + WS + "*" + BOLD + "?" + WS + "*(?=\\r?$)" + // 標題獨佔一行
  ")" + WS + "*" + BOLD + "?";

// 每個 key 一個捕捉群組 → 用「哪一組有值」判斷是哪個標題；別名是最後一組
const LABEL_GROUPS = [...SECTION_LABELS.map(([, labels]) => labels), RISK_ALIAS_LABELS]
  .map((labels) => "(" + labels.join("|") + ")")
  .join("|");

const buildHeadingRegex = () => new RegExp(LINE_PREFIX + "(?:" + LABEL_GROUPS + ")" + PAREN + DELIM, "gim");

// 「商品：XAUUSD　進場條件：…　策略邏輯：…」：全形空白後面緊接已知標題＋冒號 → 當成換行
const ALL_LABELS = [...SECTION_LABELS.map(([, labels]) => labels.join("|")), ...RISK_ALIAS_LABELS].join("|");
const CRAMMED_SPLIT = new RegExp("\\u3000+(?=(?:" + ALL_LABELS + ")" + PAREN + WS + "*[\\uFF1A:])", "gi");

// 找不到行首策略邏輯標題時的退路（見檔頭）
const LOOSE_LOGIC = new RegExp(
  "(?:策略邏輯|策略逻辑|Strategy[ \\t]+Logic)" + PAREN + WS + "*" + BOLD + "?" + WS + "*[\\uFF1A:\\u2014\\u2013]" + WS + "*" + BOLD + "?",
  "i"
);

/** 去掉每行行尾空白（Markdown 硬換行「␠␠\n」、\r）再整段 trim */
const cleanBody = (s) => s.replace(/[ \t　\r]+$/gm, "").trim();

const indentWidth = (s) => [...s].reduce((w, ch) => w + (ch === "\t" ? 4 : ch === "\u3000" ? 2 : 1), 0);

/** 標題行的「格式」：縮排寬度｜記號種類（無／#／編號／符號）｜粗體｜括號。規則 (c) 用。 */
function headingStyle(indent, marks, opener) {
  const m = marks.replace(/[ \t\u3000]/g, "");
  let kind = "";
  if (m) {
    if (m.includes("#")) kind = "#";
    else if (/[0-9一二三四五六七八九十\u2460-\u24FF\u2776-\u2793]/.test(m)) kind = "n";
    else kind = "s";
  }
  const bold = /\*\*|__/.test(opener) ? "b" : "";
  const bracket = /[\u3010\u3014[]/.test(opener) ? "[" : "";
  return `${indentWidth(indent)}|${kind}|${bold}|${bracket}`;
}

function findHeadings(src) {
  const re = buildHeadingRegex();
  const hits = [];
  let m;
  while ((m = re.exec(src)) !== null) {
    const labelIndex = m.slice(4).findIndex((g) => g !== undefined);
    const alias = labelIndex === ALIAS_INDEX;
    const labelStart = m.index + m[1].length + m[2].length + m[3].length;
    const headingEnd = m.index + m[0].length;
    let lead = "";
    if (alias) {
      // 別名的內容保留標題字樣：「停損：進場價 − 2×ATR(14)」
      const heading = src.slice(labelStart, headingEnd).replace(/\*\*|__|[\u3011\u3015\]]/g, "").trim();
      lead = /[\uFF1A]$/.test(heading) ? heading : /:$/.test(heading) ? heading + " " : heading + "\n";
    }
    hits.push({
      key: alias ? "risk" : SECTION_KEYS[labelIndex],
      alias,
      lineStart: m.index,
      bodyStart: headingEnd,
      lead,
      style: headingStyle(m[1], m[2], m[3]),
    });
    if (m[0].length === 0) re.lastIndex += 1; // 保險：不會發生（標題至少一個字），但避免零長度比對卡死
  }
  return hits;
}

/**
 * 把摘要切成各段。只回傳找得到且有內容的段落。
 * @param {string} text
 * @returns {{ instrument?, timeframe?, capital?, lotSize?, orderType?, startDate?, endDate?,
 *             entry?, exit?, risk?, logic?: string }}
 */
export function parseAiSummary(text) {
  const out = {};
  if (typeof text !== "string" || !text) return out;
  const src = text.replace(CRAMMED_SPLIT, "\n");
  const hits = findHeadings(src);

  const bodyOf = (lead, from, to) => {
    const rest = cleanBody(src.slice(from, to));
    return rest ? cleanBody(lead + rest) : "";
  };
  const seen = new Set();
  let current = null; // 目前這段 { key, lead, bodyStart }
  let bodyStarted = false;
  let logicStyle = null;

  const closeCurrent = (end) => {
    if (!current) return;
    const body = bodyOf(current.lead, current.bodyStart, end);
    if (body && !out[current.key]) out[current.key] = body;
  };

  hits.forEach((hit, i) => {
    const nextStart = i + 1 < hits.length ? hits[i + 1].lineStart : src.length;
    let boundary;
    if (hit.alias) {
      boundary = !seen.has("risk") && logicStyle === null; // 規則 (d)
    } else if (HEADER_KEYS.has(hit.key) && bodyStarted) {
      boundary = false; // 規則 (a)
    } else {
      const existing =
        out[hit.key] || (current && current.key === hit.key ? bodyOf(current.lead, current.bodyStart, hit.lineStart) : "");
      if (existing) boundary = bodyOf("", hit.bodyStart, nextStart) === existing; // 規則 (b)
      else if (logicStyle !== null) boundary = SHOWN_AFTER_LOGIC.has(hit.key) && hit.style === logicStyle; // 規則 (c)
      else boundary = true;
    }
    if (!boundary) return; // 內文：併進目前這段

    closeCurrent(hit.lineStart);
    current = { key: hit.key, lead: hit.lead, bodyStart: hit.bodyStart };
    seen.add(hit.key);
    if (!HEADER_KEYS.has(hit.key)) bodyStarted = true;
    if (hit.key === "logic" && logicStyle === null) logicStyle = hit.style;
  });
  closeCurrent(src.length);
  return out;
}

/**
 * AI 助理摘要卡要顯示的內容。
 *   - 找得到行首的「策略邏輯」→ logic 只放邏輯段；risk 放「風控」段（找不到就是 null，畫面不顯示那一塊）。
 *   - 找不到行首標題、但文中有「策略邏輯：」這種標記（格式怪、無法逐行解析）→ logic 取標記之後的文字
 *     （舊寫法的精神，不會比舊版差）；risk 只在解析得到、且不在 logic 文字裡時才顯示（避免重複）。
 *   - 都找不到（拒答訊息「⚠️ 輸入格式不符…」、DEMO 假摘要、舊格式）→ logic 退回全文、risk 一律 null
 *     （全文裡本來就含風控，再另外顯示會重複）。這跟舊行為一致：不會變成什麼都不顯示。
 * @returns {{ logic: string, risk: string|null, fallbackFull: boolean }}
 */
export function getSummaryDisplay(text) {
  const full = typeof text === "string" ? text.trim() : "";
  const sections = parseAiSummary(full);
  if (sections.logic) return { logic: sections.logic, risk: sections.risk || null, fallbackFull: false };

  const m = LOOSE_LOGIC.exec(full);
  const loose = m ? cleanBody(full.slice(m.index + m[0].length)) : "";
  if (loose) {
    const risk = sections.risk && !loose.includes(sections.risk) ? sections.risk : null;
    return { logic: loose, risk, fallbackFull: false };
  }
  return { logic: full, risk: null, fallbackFull: true };
}
